<template>
  <div ref="carouselContainer" :class="variant">
    <UiCarousel
      :slides-to-show-count="slidesToShow"
      :slides-to-scroll-count="slidesToScroll"
      :arrow="carouselArrow"
      :is-infinite="isCarouselInfinite"
      :show-dots="!isMultiVariantBillboard"
      :autoplay-type="autoPlayType"
      @arrow-clicked="handleArrowClicked"
      @slide-changed="handleSlideChanged"
      @pagination-clicked="handlePaginationClicked"
      :style="{
        '--slide-width': `${FIXED_SLIDE_WIDTH}px`,
        '--container-width': `${FIXED_SLIDE_WIDTH * props.contents.length}px`,
        '--slide-width-mobile': `${FIXED_SLIDE_WIDTH_MOBILE}px`,
        '--container-width-mobile': `${
          FIXED_SLIDE_WIDTH_MOBILE * props.contents.length
        }px`,
      }"
    >
      <UiCarouselSlide
        v-element-visibility="[
        (isVisible: boolean) =>
        isVisible && onVisibilityTracking(content.internalName, index),
        {
          threshold: 1.0,
        },
        ]"
        v-for="(content, index) in contents"
        :key="content.id"
        :class="[
          { 'ssr-breakpoints-patch': applySsrBreakpointsPatch },
          variant,
        ]"
      >
        <div class="billboard-carousel-content">
          <UiContentCard
            v-if="isMultiVariantBillboard"
            :title="content.banner.title"
            :subtitle="content.banner.subtitle"
            :link-url="content.banner.urlPath"
            :is-open-in-new-tab="content.isOpenInNewTab"
            :image="getSmartImageAttrs(content.banner.image)"
            :text-color="getTextColor(content.banner.textColor)"
            :background-color="
              getBackgroundColor(content.banner.backgroundColor)
            "
            @click="emit('banner-clicked', content.internalName, index)"
          >
            <UiLabel
              :text="content.ctaText"
              class="headline-l cta-label"
              :icons="{
                right: {
                  name: 'caret',
                },
              }"
            />
          </UiContentCard>
          <UiSmartLink
            v-else
            v-bind="
              getLinkAttributes(content.banner.urlPath, content.isOpenInNewTab)
            "
            @click="emit('banner-clicked', content.internalName, index)"
          >
            <UiSmartImage
              v-bind="getSmartImageAttrs(content.banner.image)"
              :lazy-loading="true"
              :skeleton="true"
            />
          </UiSmartLink>
          <button
            v-if="content.tnc"
            :style="{ color: getTextColor(content.banner.textColor) }"
            @click="showModal(content, index)"
            class="additional-link body-s"
          >
            {{ content.tnc?.label }}
          </button>
        </div>
      </UiCarouselSlide>
    </UiCarousel>

    <UiModal
      v-if="isModalOpen"
      :title="currentBillboardContent?.tnc?.label"
      @close="closeModal"
    >
      <template #body>
        <div
          class="tnc-content"
          v-html="currentBillboardContent?.tnc?.content"
        />
      </template>
    </UiModal>
  </div>
</template>

<script lang="ts" setup>
import UiCarousel, {
  ArrowPosition,
  NavArrowsDisplayType,
  UiCarouselSlide,
  type Arrow,
  AutoplayType,
} from '@sephora-asia/ui-carousel'
import UiLabel from '@sephora-asia/ui-label'
import UiSmartLink from '@sephora-asia/ui-smart-link'
import UiSmartImage, {
  type PropsSmartImage,
} from '@sephora-asia/ui-smart-image'
import UiContentCard from '@sephora-asia/ui-content-card'
import { computed, onMounted, onBeforeUnmount, ref, type Ref } from 'vue'
import type { BillboardContent, PropsBillboard } from './types'
import UiModal from '@sephora-asia/ui-modal'
import { vElementVisibility } from '@vueuse/components'

interface Emits {
  (e: 'modal-opened', internalName: string, index: number): void
  (e: 'banner-clicked', internalName: string, index: number): void
  (e: 'banner-impression', internalName: string, index: number): void
  (e: 'arrow-clicked', internalName: string, index: number): void
  (e: 'slide-changed', internalName: string, index: number): void
}
const emit = defineEmits<Emits>()

const props = withDefaults(defineProps<PropsBillboard>(), {
  variant: 'large',
  isMobile: false,
})

const FIXED_SLIDE_WIDTH = 480
const FIXED_SLIDE_WIDTH_MOBILE = 352
const MOBILE_MAX_WIDTH = 768

const getTextColor = (color?: string) => color || 'var(--color-base-white)'
const getBackgroundColor = (color?: string) =>
  color || 'var(--color-base-black)'

const isMultiVariantBillboard = computed(() => props.variant === 'multi')
const isModalOpen = ref(false)
const currentBillboardContent: Ref<BillboardContent | null> = ref(null)
const slidesToShow = ref(isMultiVariantBillboard.value ? 1.1 : 1) // >1 to keep inline-margin for multi variant
const slidesToScroll = ref(1)
const carouselContainer = ref<HTMLDivElement | null>(null) // Reference to carousel container
const applySsrBreakpointsPatch = ref(isMultiVariantBillboard.value)
const currentSingleBillboardIndex = ref(0)

const isAutoplayEnabled = ref(!isMultiVariantBillboard.value)

const autoPlayType = computed(() =>
  isAutoplayEnabled.value && props.contents.length > 1
    ? AutoplayType.PAUSE_AUTOPLAY_ON_HOVER
    : AutoplayType.NO_AUTOPLAY
)

const isCarouselInfinite = computed(
  () =>
    props.contents.length > 1 &&
    (props.isInfinite !== undefined
      ? props.isInfinite
      : !isMultiVariantBillboard.value)
)

const carouselArrow = computed((): Arrow => {
  return {
    position: ArrowPosition.WITHIN,
    displayType: props.isMobile
      ? NavArrowsDisplayType.HIDE
      : NavArrowsDisplayType.SHOW_ON_HOVER,
    size: '',
  }
})

const aspectRatioWeb = computed(() => {
  if (props.customAspectRatio?.web) {
    return props.customAspectRatio?.web
  }

  switch (props.variant) {
    case 'medium':
      return '21/9'
    case 'small':
      return '15/4'
    case 'multi':
      return '4/3'
    default:
      return '2/1'
  }
})

const aspectRatioMobileAndTablet = computed(() => {
  if (props.customAspectRatio?.mobileAndTablet) {
    return props.customAspectRatio?.mobileAndTablet
  }

  return isMultiVariantBillboard.value ? '2/1' : '4/3'
})

const getLinkAttributes = (url: string, isOpenInNewTab?: boolean) => ({
  to: url,
  rel: isOpenInNewTab ? 'noopener noreferrer' : null,
  target: isOpenInNewTab ? '_blank' : null,
})

const getSmartImageAttrs = (image: Omit<PropsSmartImage, 'aspectRatio'>) => ({
  ...image,
  aspectRatio: aspectRatioWeb.value,
  aspectRatioTablet: aspectRatioMobileAndTablet.value,
  aspectRatioMobile: aspectRatioMobileAndTablet.value,
})

const showModal = (content: BillboardContent, index: number) => {
  isModalOpen.value = true
  isAutoplayEnabled.value = false
  currentBillboardContent.value = content
  emit('modal-opened', content.internalName, index)
}

const closeModal = () => {
  isModalOpen.value = false
  currentBillboardContent.value = null
}

const handleArrowClicked = (index: number) => {
  isAutoplayEnabled.value = false

  emit('arrow-clicked', props.contents?.[index]?.internalName, index)
}

const handleSlideChanged = (index: number) => {
  emit('slide-changed', props.contents?.[index]?.internalName, index)

  if (!isMultiVariantBillboard.value) {
    currentSingleBillboardIndex.value = index
  }
}

function handlePaginationClicked() {
  isAutoplayEnabled.value = false
}

function updateSlidesToShow() {
  const containerWidth =
    carouselContainer.value?.clientWidth || window.innerWidth
  const slideWidth =
    containerWidth < MOBILE_MAX_WIDTH
      ? FIXED_SLIDE_WIDTH_MOBILE
      : FIXED_SLIDE_WIDTH // including margin offset
  const slidesCount = Number((containerWidth / slideWidth).toFixed(3))
  slidesToShow.value = Math.min(props.contents.length, slidesCount)
  slidesToScroll.value = Math.floor(slidesCount)
}

function onVisibilityTracking(internalName: string, index: number) {
  if (
    isMultiVariantBillboard.value ||
    (!isMultiVariantBillboard.value &&
      currentSingleBillboardIndex.value === index) // need this check because visibility return true for all slides in single billboard
  ) {
    emit('banner-impression', internalName, index)
  }
}

onMounted(() => {
  if (isMultiVariantBillboard.value) {
    updateSlidesToShow()
    window.addEventListener('resize', updateSlidesToShow)
    // Remove breakpoint after mounting to let carousel handle slide width
    applySsrBreakpointsPatch.value = false
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSlidesToShow)
})
</script>

<style lang="scss" scoped>
.billboard-carousel-content {
  position: relative;

  .additional-link {
    border: none;
    background-color: rgba(0, 0, 0, 0.6);
    color: var(--color-base-white);
    padding: 4px 8px;
    position: absolute;
    left: 0;
    bottom: 0;
    width: fit-content;
    z-index: 1;
    cursor: pointer;
    text-decoration: underline;
  }

  :deep(.content-card-link) {
    &:hover {
      text-decoration: none;
    }
    .image {
      border-top-right-radius: var(--size-radius-medium);
      border-top-left-radius: var(--size-radius-medium);
    }

    .content {
      height: 136px; // Fixed height specification from figma
      display: flex;
      flex-direction: column;
      text-align: left;
      border-bottom-right-radius: var(--size-radius-medium);
      border-bottom-left-radius: var(--size-radius-medium);
      padding: 16px;

      .title {
        @extend .headline-xl;
      }

      .cta-label {
        display: flex;
        align-items: center;
        color: inherit;
        width: fit-content;
        margin-top: auto;

        .icon-container {
          width: 16px;

          svg {
            transform: rotate(-90deg);
            fill: currentcolor;
          }
        }
      }
    }
  }
}

.tnc-content {
  --scrollbar-offset: 12px;
  --scrollbar-offset-mweb: 8px;
  max-height: 80vh;
  overflow-y: auto;
  scrollbar-width: 5px;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--color-base-grey-30);
    border-radius: var(--size-radius-high);
  }

  // To avoid scrollbar getting too close to content without overriding modal's css
  padding-right: var(--scrollbar-offset);
  margin-right: calc(var(--scrollbar-offset) * -1);

  @include mobile {
    padding-right: var(--scrollbar-offset-mweb);
    margin-right: calc(var(--scrollbar-offset-mweb) * -1);
  }
}

.multi {
  .additional-link {
    right: 16px;
    left: unset;
    bottom: 16px;
    color: inherit;
    background-color: unset;
  }

  :deep(.carousel__slide.multi.ssr-breakpoints-patch) {
    width: var(--slide-width) !important;

    @include media(mobile) {
      width: var(--slide-width-mobile) !important;
    }
  }

  :deep(.carousel__viewport) {
    display: flex;
    justify-content: center;
  }

  :deep(.carousel__track) {
    width: var(--container-width) !important;
    max-width: 100%;

    @include media(mobile) {
      width: var(--container-width-mobile) !important;
    }
  }
}
</style>
